import { Dashboard, TabGroup, TabsDisplay } from "api/model/dashboard/Dashboard";
import { Button } from "primereact/button";
import { JSONEditorMode } from "utils/enums";
import { PermissionGuard } from "components/wrappers/PermissionGuard";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import React, { useState } from "react";
import { T } from "services/lang/LanguageService";
import { v4 as uuidv4 } from "uuid";
import JSONEditor from "components/basic/json-editor/JSONEditor";

interface DashboardTabConfiguratorProps {
  onHide: () => void;
  dashboard: Dashboard;
  setDashboard: (dashboard: Dashboard) => void;
}

const tabLabelHandler = (value: string) => {
  switch (value) {
    case TabsDisplay.ROWS:
      return T("tabs-display.rows");
    case TabsDisplay.DROPDOWN:
      return T("tabs-display.dropdown");
    case TabsDisplay.SUB_TAB_DROPDOWN:
      return T("tabs-display.sub-tab-dropdown");
    default:
      return T("message.unknown-type");
  }
};

const typesOptions = Object.values(TabsDisplay).map(value => ({
  label: tabLabelHandler(value),
  value,
}));

const DashboardConfigurator = ({
  onHide,
  dashboard,
  setDashboard,
}: DashboardTabConfiguratorProps) => {
  const [selectedTabDisplayType, setSelectedTabDisplayType] = useState<TabsDisplay>(
    dashboard.tabsDisplay ?? TabsDisplay.ROWS
  );
  const [isArrowsVisible, setIsArrowsVisible] = useState<boolean>(
    dashboard.showTabNavigation ?? false
  );

  const [currentTabGroups, setCurrentTabGroups] = useState<TabGroup[]>(dashboard.tabGroups ?? []);
  const [isJSONEditorVisible, setIsJSONEditorVisible] = useState<boolean>(false);
  const handleSaveDashboardConfigurator = () => {
    const newDashboard = {
      ...dashboard,
      tabsDisplay: selectedTabDisplayType,
      tabGroups: currentTabGroups,
      showTabNavigation: isArrowsVisible,
    };
    setDashboard(newDashboard);
    onHide();
  };

  const onRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const newData = e.newData as TabGroup;
    setCurrentTabGroups(prev => prev.map(group => (group.uuid === newData.uuid ? newData : group)));
  };

  const groupRemoveButtonTemplate = (groupUuid: string) => {
    return (
      <Button
        type="button"
        icon="pi pi-times"
        tooltip={T("action.remove")}
        tooltipOptions={{ position: "bottom" }}
        className="p-button-danger p-button-sm p-button-text"
        onClick={() =>
          confirmDialog({
            message: T("confirm-dialog.are-you-sure-you-want-to-delete-this-group"),
            header: T("confirm-dialog.remove-group"),
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            acceptLabel: T("action.remove"),
            rejectLabel: T("word.no"),
            accept: () => {
              setCurrentTabGroups(prevGroups =>
                prevGroups.filter(group => group.uuid !== groupUuid)
              );
            },
          })
        }
      />
    );
  };

  const dialogFooterTemplate = () => {
    return (
      <div className="flex flex-row gap-2 h-3rem justify-content-end ml-auto">
        <Button
          label={T("word.cancel")}
          icon="pi pi-times"
          onClick={onHide}
          className="component-editor-button icon-red"
        />
        <Button
          label={T("action.save")}
          icon="pi pi-check"
          onClick={handleSaveDashboardConfigurator}
          className="component-editor-button icon-green"
        />
      </div>
    );
  };

  const tabGroupTitleEditorTemplate = (options: any) => {
    return (
      <InputText
        value={options.value}
        type="text"
        className="p-inputtext-sm w-full"
        placeholder={T("action.write-title")}
        name="title"
        onChange={e => {
          options.editorCallback(e.target.value);
        }}
      />
    );
  };

  return (
    <>
      <Dialog
        header={T("action.configure-dashboard")}
        visible
        onHide={onHide}
        footer={dialogFooterTemplate}
        className="w-30rem"
        style={{ maxHeight: "80vh" }}
        resizable
        draggable
      >
        <ConfirmDialog />
        <div className="flex flex-column gap-3">
          <div className="flex flex-column gap-2">
            <label htmlFor="tabsDisplayType">{T("action.change-tabs-display-type")}</label>
            <Dropdown
              className="w-full"
              value={selectedTabDisplayType}
              options={typesOptions}
              placeholder={T("action.change-tabs-display-type")}
              onChange={e => setSelectedTabDisplayType(e.value)}
            />
          </div>
          <div className="flex flex-row justify-content-between">
            {selectedTabDisplayType === TabsDisplay.DROPDOWN && (
              <div className="flex flex-column gap-2">
                <label htmlFor="showTabNavigation">{T("action.show-tabs-navigation-arrows")}</label>
                <InputSwitch
                  id="showTabNavigation"
                  checked={isArrowsVisible}
                  onChange={e => setIsArrowsVisible(e.value)}
                />
              </div>
            )}
            <PermissionGuard permissions="HIDDEN_FEATURE_ACCESS">
              <Button
                label={T("word.json-editor")}
                icon="pi pi-code"
                className="component-editor-button icon-blue"
                onClick={() => setIsJSONEditorVisible(true)}
              />
            </PermissionGuard>
          </div>
          <div className="flex flex-column gap-2">
            <div className="flex flex-row justify-content-between">
              <label htmlFor="tabGroups">{T("label.tab-groups")}</label>
              <Button
                icon="pi pi-plus"
                label={T("action.add")}
                className="dashboard-editor-button icon-green"
                tooltip={T("action.add-tab-group")}
                tooltipOptions={{ position: "bottom" }}
                onClick={() =>
                  setCurrentTabGroups([...currentTabGroups, { title: "New group", uuid: uuidv4() }])
                }
              />
            </div>
            {currentTabGroups.length === 0 ? (
              <span style={{ textAlign: "center" }}>{T("label.no-tab-groups")}</span>
            ) : (
              <DataTable
                value={currentTabGroups.filter(group => !group.isTabPlaceholder)}
                dataKey="uuid"
                className="p-datatable-sm"
                editMode="row"
                onRowEditComplete={onRowEditComplete}
                size="small"
              >
                <Column
                  field="title"
                  header="Title"
                  style={{ width: "73%" }}
                  editor={options => tabGroupTitleEditorTemplate(options)}
                />
                <Column
                  body={rowData => groupRemoveButtonTemplate(rowData.uuid)}
                  style={{ width: "2%" }}
                />

                <Column rowEditor style={{ width: "25%", textAlign: "end" }} />
              </DataTable>
            )}
          </div>
        </div>
      </Dialog>
      {isJSONEditorVisible && (
        <JSONEditor
          config={dashboard}
          isVisible={isJSONEditorVisible}
          setIsVisible={setIsJSONEditorVisible}
          mode={JSONEditorMode.DASHBOARD}
        />
      )}
    </>
  );
};

export default DashboardConfigurator;
